<template>
  <div class="test-session-page">
    <div v-if="loading" class="loading-container">
      <ProgressSpinner />
      <p>Завантаження тесту...</p>
    </div>

    <div v-else-if="session" class="test-container">
      <div class="test-header">
        <div class="test-info">
          <h1>{{ session.name }}</h1>
          <p class="test-description">{{ session.test_type?.description }}</p>
          <div class="test-meta">
            <Tag :value="session.test_type?.category" severity="info" />
                         <span class="question-counter">
               Питання {{ currentQuestionIndex + 1 }} з {{ filteredQuestions.length }}
             </span>
          </div>
        </div>
        
        <div class="progress-section">
          <ProgressBar 
            :value="progressPercentage" 
            :showValue="false"
            class="progress-bar"
          />
          <div class="time-remaining" v-if="timeRemaining">
            <i class="pi pi-clock"></i>
            <span>{{ formatTime(timeRemaining) }}</span>
          </div>
        </div>
      </div>

      <div class="question-container">
        <div v-if="currentQuestion" class="question-card">
          <div class="question-header">
            <h2>{{ currentQuestion.text }}</h2>
            <div class="question-type">
              <Tag :value="getQuestionTypeLabel(currentQuestion.question_type)" severity="secondary" />
            </div>
          </div>

          <div class="question-content">
            <!-- Likert Scale (1-5) -->
            <div v-if="currentQuestion.question_type === 'likert_scale'" class="likert-scale">
              <div class="scale-labels">
                <span>Повністю не згоден</span>
                <span>Повністю згоден</span>
              </div>
              <div class="scale-options">
                                 <div
                   v-for="value in 5"
                   :key="value"
                   class="scale-option"
                   :class="{ 'selected': answers[currentQuestion?.id] === value }"
                   @click="selectAnswer(value)"
                 >
                  {{ value }}
                </div>
              </div>
            </div>

            <!-- Likert Scale (1-7) -->
            <div v-else-if="currentQuestion.question_type === 'likert_scale_7'" class="likert-scale">
              <div class="scale-labels">
                <span>Повністю не згоден</span>
                <span>Повністю згоден</span>
              </div>
              <div class="scale-options">
                                 <div
                   v-for="value in 7"
                   :key="value"
                   class="scale-option"
                   :class="{ 'selected': answers[currentQuestion?.id] === value }"
                   @click="selectAnswer(value)"
                 >
                  {{ value }}
                </div>
              </div>
            </div>

            <!-- eNPS Scale (0-10) -->
            <div v-else-if="currentQuestion.question_type === 'enps_scale'" class="enps-scale">
              <div class="scale-labels">
                <span>Зовсім не ймовірно</span>
                <span>Надзвичайно ймовірно</span>
              </div>
              <div class="scale-options">
                <div
                  v-for="value in 11"
                  :key="value - 1"
                  class="scale-option"
                  :class="{ 'selected': answers[currentQuestion?.id] === value - 1 }"
                  @click="selectAnswer(value - 1)"
                >
                  {{ value - 1 }}
                </div>
              </div>
              
            </div>

            <!-- Yes/No -->
            <div v-else-if="currentQuestion.question_type === 'yes_no'" class="yes-no-options">
              <Button
                label="Так"
                :class="{ 'p-button-success': answers[currentQuestion?.id] === 'Так' }"
                @click="selectAnswer('Так')"
                class="option-button"
              />
              <Button
                label="Ні"
                :class="{ 'p-button-danger': answers[currentQuestion?.id] === 'Ні' }"
                @click="selectAnswer('Ні')"
                class="option-button"
              />
            </div>

                         <!-- Numeric -->
             <div v-else-if="currentQuestion.question_type === 'numeric'" class="numeric-input">
               <InputNumber
                 v-model="answers[currentQuestion?.id]"
                 :min="1"
                 :max="10"
                 placeholder="Введіть число від 1 до 10"
                 class="numeric-field"
               />
             </div>

                         <!-- Text -->
             <div v-else-if="currentQuestion.question_type === 'text'" class="text-input">
               <Textarea
                 v-model="answers[currentQuestion?.id]"
                 placeholder="Введіть вашу відповідь..."
                 :rows="4"
                 class="text-field"
               />
             </div>
          </div>
        </div>
      </div>

      <div class="navigation-actions">
        <Button
          v-if="currentQuestionIndex > 0"
          label="Назад"
          icon="pi pi-chevron-left"
          class="p-button-secondary"
          @click="previousQuestion"
        />
        
                 <div class="question-dots">
                      <div
              v-for="(question, index) in filteredQuestions"
              :key="question.id"
              class="dot"
              :class="{
                'active': index === currentQuestionIndex,
                'answered': answers[question?.id] !== undefined && answers[question?.id] !== null && answers[question?.id] !== ''
              }"
              @click="goToQuestion(index)"
            ></div>
         </div>

                 <Button
           v-if="currentQuestionIndex < filteredQuestions.length - 1"
           label="Далі"
           icon="pi pi-chevron-right"
           iconPos="right"
           class="p-button-primary"
           :disabled="!isCurrentQuestionAnswered"
           @click="nextQuestion"
         />
        
        <Button
          v-else
          label="Завершити тест"
          icon="pi pi-check"
          class="p-button-success"
          :disabled="!isAllQuestionsAnswered"
          @click="submitTest"
        />
      </div>
    </div>

    <div v-else class="error-container">
      <i class="pi pi-exclamation-triangle error-icon"></i>
      <h2>Тест не знайдено</h2>
      <p>Запитаний тест не існує або недоступний.</p>
      <Button label="Повернутися до тестів" @click="$router.push('/tests')" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'TestSession',
  data() {
    return {
      loading: true,
      session: null,
      questions: [],
      currentQuestionIndex: 0,
      answers: new Proxy({}, {
        set(target, property, value) {
          // BLOCK ANY "Hi" VALUES FROM BEING SET
          if (value === 'Hi' || value === 'hi' || value === 'HI') {
            return false // Prevent the assignment
          }
          target[property] = value
          return true
        },
        get(target, property) {
          return target[property]
        }
      }),
      timeRemaining: null,
      timer: null,
      hiCheckInterval: null
    }
  },
  computed: {
    ...mapState(['currentUser']),
    currentQuestion() {
      const question = this.filteredQuestions[this.currentQuestionIndex] || null
      return question
    },
    progressPercentage() {
      return ((this.currentQuestionIndex + 1) / this.filteredQuestions.length) * 100
    },
    isCurrentQuestionAnswered() {
      if (!this.currentQuestion) return false
      const answer = this.answers[this.currentQuestion.id]
      return answer !== undefined && answer !== null && answer !== ''
    },
    isAllQuestionsAnswered() {
      return this.filteredQuestions.every(question => {
        const answer = this.answers[question.id]
        return answer !== undefined && answer !== null && answer !== ''
      })
    },
         filteredQuestions() {
       if (!this.session || this.session.test_type?.category !== 'enps') {
         return this.questions
       }
      
      // Find the main eNPS question
      const mainQuestion = this.questions.find(q => q.question_type === 'enps_scale')
      if (!mainQuestion) {
        return this.questions
      }
      
      const mainAnswer = this.answers[mainQuestion.id]
      
      // If main question is not answered yet, show only main question
      if (mainAnswer === undefined || mainAnswer === null) {
        return [mainQuestion]
      }
      
             // Filter questions based on main answer
       let filteredQuestions = [mainQuestion]
       
       if (mainAnswer >= 9) {
         // Promoter questions (9-10) - показуємо питання 19, 20, 21
         const promoterQuestions = this.questions.filter(q => 
           q.question_type === 'text' && (
             q.text.includes('подобається') || 
             q.text.includes('мотивує') || 
             q.text.includes('особливо добре')
           )
         )
         filteredQuestions = filteredQuestions.concat(promoterQuestions)
       } else if (mainAnswer >= 7) {
         // Passive questions (7-8) - показуємо питання 23, 26
         const passiveQuestions = this.questions.filter(q => 
           q.question_type === 'text' && (
             q.text.includes('кращим') || 
             q.text.includes('покращень') ||
             q.text.includes('вищу оцінку')
           )
         )
         filteredQuestions = filteredQuestions.concat(passiveQuestions)
       } else {
         // Detractor questions (0-6) - показуємо питання 22, 27, 28, 29
         const detractorQuestions = this.questions.filter(q => 
           q.question_type === 'text' && (
             q.text.includes('не дуже добре') ||
             q.text.includes('розчарування') ||
             q.text.includes('змінити') ||
             q.text.includes('причини')
           )
         )
         filteredQuestions = filteredQuestions.concat(detractorQuestions)
       }
       
      return filteredQuestions
    }
  },
  watch: {
    answers: {
      handler(newAnswers, oldAnswers) {
        
        // Check for "Hi" values immediately
        this.checkForHiValues()
        
        // Immediately clear any "Hi" values
        this.clearHiValues()
        
        // Check for suspicious changes
        const yesNoQuestions = this.questions.filter(q => q.question_type === 'yes_no')
        const validAnswers = ['Так', 'Ні', 'yes', 'no', 'true', 'false', '1', '0']
        
        for (const question of yesNoQuestions) {
          const newAnswer = newAnswers[question.id]
          const oldAnswer = oldAnswers?.[question.id]
          
          if (newAnswer !== oldAnswer) {
            
            if (newAnswer && !validAnswers.includes(newAnswer)) {
              console.error(`🚨 SUSPICIOUS CHANGE DETECTED: Question ${question.order} got invalid answer "${newAnswer}"`)
            }
          }
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['submitTest']),
    checkForHiValues() {
      let foundHiValues = false
      
      for (const [_questionId, answer] of Object.entries(this.answers)) {
        if (answer === 'Hi' || answer === 'hi' || answer === 'HI') {
          foundHiValues = true
          console.error(`🚨 FOUND "Hi" VALUE: Question ID ${_questionId}, Answer: "${answer}"`)
          
          // Try to find the question details
          const question = this.questions.find(q => q.id == _questionId)
          if (question) {
            console.error(`Question details: Order ${question.order}, Type: ${question.question_type}, Text: ${question.text.substring(0, 50)}...`)
          }
        }
      }
      
      if (foundHiValues) {
        console.error(`🚨 TOTAL "Hi" VALUES FOUND: ${Object.values(this.answers).filter(a => a === 'Hi' || a === 'hi' || a === 'HI').length}`)
        console.error(`Full answers object:`, this.answers)
      } else {
        console.log('✅ No "Hi" values found')
      }
      
      return foundHiValues
    },
    forceCleanAnswers() {
      const cleanedAnswers = {}
      
      for (const [_questionId, answer] of Object.entries(this.answers)) {
        if (answer !== 'Hi' && answer !== 'hi' && answer !== 'HI') {
          cleanedAnswers[_questionId] = answer
        } else {
          console.warn(`🗑️ Removed "Hi" value for question ${_questionId}`)
        }
      }
      
      // Replace the entire answers object
      Object.keys(this.answers).forEach(key => {
        delete this.answers[key]
      })
      
      Object.assign(this.answers, cleanedAnswers)
      console.log('Answers cleaned:', this.answers)
    },
    resetTestIfNeeded() {
      let hasHiValues = false
      for (const [, answer] of Object.entries(this.answers)) {
        if (answer === 'Hi' || answer === 'hi' || answer === 'HI') {
          hasHiValues = true
          break
        }
      }
      
      if (hasHiValues) {
        console.warn('🚨 "Hi" VALUES DETECTED - RESETTING TEST')
        this.forceResetComponent()
        this.$toast.add({
          severity: 'warn',
          summary: 'Попередження',
          detail: 'Тест було скинуто через недійсні відповіді. Почніть заново.',
          life: 5000
        })
      }
    },
    forceResetComponent() {
      // Clear all properties from the Proxy answers object
      Object.keys(this.answers).forEach(key => {
        delete this.answers[key]
      })
      this.currentQuestionIndex = 0
      console.log('Component state reset')
    },
    clearHiValues() {
      let clearedCount = 0
      
      for (const [_questionId, answer] of Object.entries(this.answers)) {
        if (answer === 'Hi' || answer === 'hi' || answer === 'HI') {
          delete this.answers[_questionId]
          clearedCount++
        }
      }
      
      if (clearedCount > 0) {
        console.log(`Cleared ${clearedCount} "Hi" values`)
        this.$toast.add({
          severity: 'warn',
          summary: 'Попередження',
          detail: `Видалено ${clearedCount} недійсних відповідей`,
          life: 3000
        })
      }
    },
    clearAllAnswers() {
      console.log('=== CLEARING ALL ANSWERS ===')
      // Clear all properties from the Proxy answers object
      Object.keys(this.answers).forEach(key => {
        delete this.answers[key]
      })
      console.log('All answers cleared')
    },
    clearInvalidAnswers() {
      // Clear any invalid answers for yes_no questions
      const validAnswers = ['Так', 'Ні', 'yes', 'no', 'true', 'false', '1', '0']
      const yesNoQuestions = this.questions.filter(q => q.question_type === 'yes_no')
      
      for (const question of yesNoQuestions) {
        const answer = this.answers[question.id]
        
        if (answer && !validAnswers.includes(answer)) {
          delete this.answers[question.id]
        }
      }
      
      console.log('Final answers object:', this.answers)
    },
    async loadSession() {
      try {
        const sessionId = this.$route.params.id
        this.loading = true
        
        // Fetch session data from API
        const sessionResponse = await this.$store.dispatch('fetchSession', sessionId)
        this.session = sessionResponse
        
        // Fetch questions for this session
        const questionsResponse = await this.$store.dispatch('fetchSessionQuestions', sessionId)
        this.questions = questionsResponse
        
        // Debug: Log loaded questions
        console.log('Loaded questions:', this.questions.map(q => ({
          id: q.id,
          order: q.order,
          type: q.question_type,
          text: q.text.substring(0, 50) + '...'
        })))
        
        // Clear all answers when loading a new session
        this.clearAllAnswers()
        
        // Clear any invalid answers
        this.clearInvalidAnswers()
        
        // Clear any "Hi" values specifically
        this.clearHiValues()
        
        // Debug: Check if main eNPS question is found
        const mainQuestion = this.questions.find(q => q.question_type === 'enps_scale')
        if (mainQuestion) {
          console.log('Main eNPS question found:', {
            id: mainQuestion.id,
            order: mainQuestion.order,
            text: mainQuestion.text.substring(0, 50) + '...'
          })
          
          // Reset question index for eNPS tests
          if (this.session?.test_type?.category === 'enps') {
            this.currentQuestionIndex = 0
            console.log('Reset question index to 0 for eNPS test')
          }
        } else {
          console.log('Main eNPS question NOT found!')
        }
        
        this.loading = false
      } catch (error) {
        console.error('Error loading session:', error)
        this.loading = false
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Не вдалося завантажити тест',
          life: 3000
        })
      }
    },
    getQuestionTypeLabel(type) {
      const labels = {
        likert_scale: 'Шкала Лікерта (1-5)',
        likert_scale_7: 'Шкала Лікерта (1-7)',
        yes_no: 'Так/Ні',
        numeric: 'Числова відповідь',
        text: 'Текстова відповідь'
      }
      return labels[type] || type
    },
         selectAnswer(value) {
      if (this.currentQuestion) {
        
        // AGGRESSIVE VALIDATION: Block any "Hi" values immediately
        if (value === 'Hi' || value === 'hi' || value === 'HI') {
          console.error(`🚫 BLOCKED "Hi" VALUE: "${value}" for question ${this.currentQuestion.order}`)
          console.error(`Stack trace:`, new Error().stack)
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: `Недопустима відповідь: "${value}". Виберіть "Так" або "Ні"`,
            life: 5000
          })
          return
        }
        
        // ADDITIONAL VALIDATION: Check for any suspicious values
        if (typeof value === 'string' && value.length > 10) {
          console.warn(`⚠️ SUSPICIOUS LONG ANSWER: "${value}" for question ${this.currentQuestion.order}`)
        }
        
        // Validate yes_no answers
        if (this.currentQuestion.question_type === 'yes_no') {
          const validAnswers = ['Так', 'Ні', 'yes', 'no', 'true', 'false', '1', '0']
          if (!validAnswers.includes(value)) {
            console.error(`🚫 INVALID ANSWER REJECTED: "${value}" for yes_no question. Expected one of: ${validAnswers.join(', ')}`)
            this.$toast.add({
              severity: 'error',
              summary: 'Помилка',
              detail: `Недійсна відповідь: "${value}". Очікується: Так або Ні`,
              life: 3000
            })
            return
          }
        }
        
        this.answers[this.currentQuestion.id] = value
        
        // If this is the main eNPS question, reset question index to show follow-up questions
        if (this.currentQuestion.question_type === 'enps_scale' && this.session?.test_type?.category === 'enps') {
          console.log('Main eNPS question answered, resetting index to 0')
          this.currentQuestionIndex = 0
          
          // Force re-computation of filteredQuestions
          this.$nextTick(() => {
            console.log('=== ПІСЛЯ СКИДАННЯ ІНДЕКСУ ===')
            console.log('Filtered questions count:', this.filteredQuestions.length)
            console.log('Current question index:', this.currentQuestionIndex)
            console.log('Current question:', this.currentQuestion?.text?.substring(0, 50) + '...')
          })
        }
        
        // If main question is not answered, ensure we're on the first question
        if (this.session?.test_type?.category === 'enps') {
          const mainQuestion = this.questions.find(q => q.question_type === 'enps_scale')
          if (mainQuestion && (this.answers[mainQuestion.id] === undefined || this.answers[mainQuestion.id] === null)) {
            if (this.currentQuestionIndex !== 0) {
              this.currentQuestionIndex = 0
              console.log('Main question not answered, forcing index to 0')
            }
          }
        }
        
        console.log('Current answers:', this.answers)
      }
    },
    nextQuestion() {
      if (this.currentQuestionIndex < this.filteredQuestions.length - 1) {
        this.currentQuestionIndex++
      }
    },
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
      }
    },
    goToQuestion(index) {
      this.currentQuestionIndex = index
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    async submitTest() {
      try {
        // CHECK FOR "Hi" VALUES AND LOG DETAILS
        this.checkForHiValues()
        
        // FORCE CLEAN ALL ANSWERS BEFORE SUBMISSION
        this.forceCleanAnswers()
        
        // AGGRESSIVE CLEANUP: Clear any "Hi" values before submission
        this.clearHiValues()
        
        // Check for any remaining "Hi" values and block submission
        for (const [_questionId, answer] of Object.entries(this.answers)) {
          if (answer === 'Hi' || answer === 'hi' || answer === 'HI') {
            const errorMsg = `БЛОКОВАНО: Питання ${_questionId} має недійсну відповідь "${answer}". Очистіть всі відповіді та спробуйте знову.`
            console.error(errorMsg)
            
            // Створюємо структуровану помилку
            const structuredError = new Error(errorMsg)
            structuredError.response = {
              data: {
                error_code: 'INVALID_ANSWER',
                detail: 'Недопустима відповідь',
                message: errorMsg
              },
              status: 400
            }
            throw structuredError
          }
        }
        
        // Validate all yes_no answers before submission
        const yesNoQuestions = this.questions.filter(q => q.question_type === 'yes_no')
        const validAnswers = ['Так', 'Ні', 'yes', 'no', 'true', 'false', '1', '0']
        
        for (const question of yesNoQuestions) {
          const answer = this.answers[question.id]
          if (answer && !validAnswers.includes(answer)) {
            const errorMsg = `Питання ${question.order}: "${answer}" - недійсна відповідь. Очікується: Так або Ні`
            console.error(errorMsg)
            
            // Створюємо структуровану помилку
            const structuredError = new Error(errorMsg)
            structuredError.response = {
              data: {
                error_code: 'INVALID_YES_NO_ANSWER',
                detail: 'Неправильна відповідь для питання "Так/Ні"',
                message: errorMsg
              },
              status: 400
            }
            throw structuredError
          }
        }
        
        // Check if this is an eNPS test
        if (this.session.test_type.category === 'enps') {
          // For eNPS tests, use the special endpoint
          const mainQuestion = this.questions.find(q => q.question_type === 'enps_scale')
          if (!mainQuestion) {
            throw new Error('Main eNPS question not found')
          }
          
          const mainScore = this.answers[mainQuestion.id]
          if (mainScore === undefined || mainScore === null) {
            throw new Error('Main question not answered')
          }
          
                     // Create test data object
           const testData = {
             session_id: this.session.id,
             main_question_score: mainScore
           }
           
           console.log('=== ВІДПРАВКА eNPS ТЕСТУ ===')
           console.log('Main score:', mainScore)
           
           // Add category-specific questions based on score
           if (mainScore >= 9) {
             // Promoter questions
             const promoterQuestions = this.questions.filter(q => 
               q.text.includes('подобається') || q.text.includes('мотивує') || q.text.includes('особливо добре')
             )
             console.log('Found promoter questions:', promoterQuestions.length)
             promoterQuestions.forEach((q, index) => {
               const answer = this.answers[q.id] || ''
               testData[`promoter_question_${index + 1}`] = answer
               console.log(`promoter_question_${index + 1}:`, answer)
             })
           } else if (mainScore >= 7) {
             // Passive questions
             const passiveQuestions = this.questions.filter(q => 
               q.text.includes('кращим') || q.text.includes('покращень') || q.text.includes('вищу оцінку')
             )
             console.log('Found passive questions:', passiveQuestions.length)
             passiveQuestions.forEach((q, index) => {
               const answer = this.answers[q.id] || ''
               testData[`passive_question_${index + 1}`] = answer
               console.log(`passive_question_${index + 1}:`, answer)
             })
           } else {
             // Detractor questions
             const detractorQuestions = this.questions.filter(q => 
               q.text.includes('розчарування') || q.text.includes('змінити') || q.text.includes('причини') || q.text.includes('не дуже добре')
             )
             console.log('Found detractor questions:', detractorQuestions.length)
             detractorQuestions.forEach((q, index) => {
               const answer = this.answers[q.id] || ''
               testData[`detractor_question_${index + 1}`] = answer
               console.log(`detractor_question_${index + 1}:`, answer)
             })
           }
           
           console.log('Final test data:', testData)
          
          await this.$store.dispatch('submitENPSTest', testData)
        } else {
          // For regular tests
          const testData = {
            session_id: this.session.id,
            responses: Object.keys(this.answers).map(_questionId => ({
              question_id: parseInt(_questionId),
              answer: this.answers[_questionId]
            }))
          }
          
          await this.$store.dispatch('submitTest', testData)
        }
        
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Тест завершено!',
          life: 3000
        })
        
        this.$router.push('/results')
      } catch (error) {
        console.error('Error submitting test:', error)
        
        // Отримуємо детальну інформацію про помилку
        let errorMessage = 'Не вдалося відправити тест'
        let errorSummary = 'Помилка'
        
        if (error.response?.data) {
          const errorData = error.response.data
          
          // Логуємо детальну інформацію
          console.error('Detailed error info:', {
            error_code: errorData.error_code,
            detail: errorData.detail,
            message: errorData.message,
            status: error.response.status
          })
          
          // Обробляємо структуру помилки (може бути масив або об'єкт)
          let message = null
          let detail = null
          let errorCode = null
          
          if (Array.isArray(errorData.message)) {
            message = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            message = errorData.message
          }
          
          if (Array.isArray(errorData.detail)) {
            detail = errorData.detail[0]
          } else if (typeof errorData.detail === 'string') {
            detail = errorData.detail
          }
          
          if (Array.isArray(errorData.error_code)) {
            errorCode = errorData.error_code[0]
          } else if (typeof errorData.error_code === 'string') {
            errorCode = errorData.error_code
          }
          
          // Використовуємо структуровані повідомлення з бекенду
          if (message) {
            errorMessage = message
          } else if (detail) {
            errorMessage = detail
          }
          
          // Встановлюємо відповідний заголовок на основі коду помилки
          if (errorCode === 'ALREADY_COMPLETED') {
            errorSummary = 'Тест вже завершено'
          } else if (errorCode === 'NOT_INVITED') {
            errorSummary = 'Немає доступу'
          } else if (errorCode === 'SESSION_NOT_STARTED') {
            errorSummary = 'Тест ще не почався'
          } else if (errorCode === 'SESSION_ENDED') {
            errorSummary = 'Тест завершено'
          } else if (errorCode === 'INVALID_ANSWER') {
            errorSummary = 'Недопустима відповідь'
          } else if (errorCode === 'INVALID_YES_NO_ANSWER') {
            errorSummary = 'Неправильна відповідь'
          } else if (errorCode === 'INCOMPLETE_RESPONSE') {
            errorSummary = 'Неповна відповідь'
          } else if (errorCode === 'QUESTION_NOT_FOUND') {
            errorSummary = 'Питання не знайдено'
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$toast.add({
          severity: 'error',
          summary: errorSummary,
          detail: errorMessage,
          life: 5000
        })
      }
    }
  },
  async mounted() {
    // Force reset component state first
    this.forceResetComponent()
    
    await this.loadSession()
    
    // Set up periodic check for "Hi" values
    this.hiCheckInterval = setInterval(() => {
      this.checkForHiValues()
      this.clearHiValues()
      this.resetTestIfNeeded()
    }, 2000) // Check every 2 seconds
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.hiCheckInterval) {
      clearInterval(this.hiCheckInterval)
    }
  }
}
</script>

<style scoped>
.test-session-page {
  padding: 2rem;
  min-height: calc(100vh - 80px);
  background: var(--surface-ground);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 1rem;
}

.test-container {
  max-width: 800px;
  margin: 0 auto;
}

.test-header {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
  margin-bottom: 2rem;
}

.test-header h1 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.test-header p {
  margin: 0;
  color: var(--text-color-secondary);
}

.test-meta {
  display: flex;
  gap: 2rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
}

.meta-item i {
  color: var(--primary-color);
}

.question-container {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
  margin-bottom: 2rem;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.question-number {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
}

.question-progress {
  color: var(--text-color-secondary);
}

.question-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.question-help {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 6px;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.question-help i {
  margin-right: 0.5rem;
}

.question-input {
  margin-bottom: 2rem;
}

.likert-scale,
.enps-scale {
  text-align: center;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

.scale-options {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.scale-option {
  flex: 1;
  padding: 1rem;
  border: 2px solid var(--surface-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1.2rem;
}

.scale-option:hover {
  border-color: var(--primary-color);
  background: rgba(16, 185, 129, 0.1);
}

.scale-option.selected {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: white;
}

.yes-no-options {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.option-button {
  min-width: 120px;
}

.numeric-input {
  display: flex;
  justify-content: center;
}

.numeric-field {
  width: 200px;
}

.text-input {
  width: 100%;
}

.text-field {
  width: 100%;
  resize: vertical;
}

.enps-scale {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.navigation-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
}

.question-dots {
  display: flex;
  gap: 0.5rem;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--surface-border);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: var(--primary-color);
  transform: scale(1.2);
}

.dot.answered {
  background: var(--green-500);
}

.error-container {
  text-align: center;
  padding: 3rem;
  color: var(--text-color-secondary);
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: var(--red-500);
}

.error-container h2 {
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

@media (max-width: 768px) {
  .test-session-page {
    padding: 1rem;
  }
  
  .test-header,
  .question-container,
  .navigation-actions {
    padding: 1rem;
  }
  
  .question-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .scale-options {
    flex-direction: column;
  }
  
  .yes-no-options {
    flex-direction: column;
  }
  
  .navigation-actions {
    flex-direction: column;
    gap: 1rem;
  }
}
</style> 